import { FC, useCallback, useReducer } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Notification from 'components/ui/base/notification/Notification';
import useProductsTemplates from 'hooks/useProductsTemplates';
import { useRequestPromise } from 'hooks/useRequest';
import { getTemplates } from 'store/template/client';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeleteProductTemplates } from 'views/custom-declaration/products/ProductTemplatesDashboard';
import useProductTemplateList from '../hooks/useProductTemplateList';
import ProductTemplatesTable from './ProductTemplatesTable';
import TemplateSearchInput from './TemplateSearchInput';
import { SFilterSelect, SFiltersRow, SFiltersWrapper } from '../styles';
import { TemplateFilters } from '../useTemplateFilters';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import Typography from 'antd/lib/typography/Typography';
import { Space } from 'antd';

const countryOptions = [
    { label: 'Ireland', value: DeclarationCountry.IRELAND },
    { label: 'UK', value: DeclarationCountry.UK },
];
const ALL_TEMPLATES_PAGE_SIZE = 9999;

interface Props {
    filters: TemplateFilters;
    productTemplateList: ReturnType<typeof useProductTemplateList>;
}

const ProductTemplatesTab: FC<Props> = ({ filters, productTemplateList }) => {
    const { country, internalType, formType, search } = filters;
    const { t } = useTranslation('customs_declarations');
    const { productTemplates, isLoading, refetch } = productTemplateList;
    const { deleteProductTemplate } = useProductsTemplates({ country, internalType, messageType: formType });

    const [deleteProductTemplates, setDeleteProductTemplates] = useReducer(
        (initialState: DeleteProductTemplates, newState: Partial<DeleteProductTemplates>) => ({
            ...initialState,
            ...newState,
        }),
        { modalVisible: false, productTemplateIds: [] }
    );

    const closeDeleteProductTemplatesModal = useCallback(
        () => setDeleteProductTemplates({ modalVisible: false, productTemplateIds: [] }),
        []
    );

    const productTemplateIds = deleteProductTemplates.productTemplateIds;
    const isMultipleDelete = productTemplateIds.length > 1;
    const deleteKeyPrefix = isMultipleDelete ? 'product-template.multiple-delete' : 'product-template.delete';

    const handleDeleteProductTemplates = async () => {
        try {
            await Promise.all(productTemplateIds.map((id) => deleteProductTemplate(id)));
            Notification({
                type: 'success',
                messageTitle: t(`${deleteKeyPrefix}-success-title`),
                description: t(`${deleteKeyPrefix}-success-message`),
            });
            refetch({ query: search });
        } catch {
            Notification({
                type: 'error',
                messageTitle: t('product.delete-error-title'),
                description: t(`${deleteKeyPrefix}-error-message`),
            });
        } finally {
            closeDeleteProductTemplatesModal();
        }
    };

    // The table shows the name of each product's declaration template, so it needs all of them for the selection.
    const { data: declarationTemplates } = useRequestPromise(
        () =>
            getTemplates(country, internalType, formType, { size: ALL_TEMPLATES_PAGE_SIZE })?.then(
                (payload) => payload.list
            ),
        {},
        [country, internalType, formType]
    );

    return (
        <>
            <SFiltersWrapper>
                <SFiltersRow>
                    <TemplateSearchInput onSearch={filters.changeSearch} />
                    <Space size={6}>
                        <SFilterSelect
                            options={countryOptions}
                            value={country}
                            onChange={(value) => filters.changeCountry(value as DeclarationCountry)}
                        />
                        <SFilterSelect
                            options={filters.internalTypeOptions}
                            value={internalType}
                            onChange={filters.changeInternalType}
                        />
                        <SFilterSelect
                            options={filters.formTypeOptions}
                            value={formType?.toUpperCase()}
                            onChange={filters.changeFormType}
                        />
                    </Space>
                </SFiltersRow>
            </SFiltersWrapper>

            <ProductTemplatesTable
                source={productTemplates}
                loading={isLoading}
                onRequestList={refetch}
                declarationTemplatesList={declarationTemplates}
                onAddProductTemplatesToDeclaration={() => {}}
                deleteProductTemplatesIds={productTemplateIds}
                setDeleteProductTemplates={setDeleteProductTemplates}
                country={country}
                messageType={formType}
                internalType={internalType}
                search={search}
                newUi
                showCommands
            />

            <ConfirmationModal
                icon={<DeleteOutlined />}
                type="error"
                open={deleteProductTemplates.modalVisible}
                onConfirm={handleDeleteProductTemplates}
                onCancel={closeDeleteProductTemplatesModal}
                title={t(`${deleteKeyPrefix}-title`)}
                content={<Typography>{t(`${deleteKeyPrefix}-text`)}</Typography>}
            />
        </>
    );
};

export default ProductTemplatesTab;
